import React, { useState } from 'react';
import { Select, message } from 'antd';

import type {
	ProductionQuestion,
	ProductionQuestionStatus,
} from '../../../interfaces/ProductionQuestion.interface';
import { useUpdateProductionQuestionMutation } from '../../../redux/api/production-questions';
import { useGetLoggedInUserQuery } from '../../../redux/api/users';
import { canEditProductionQuestion } from '../../../utils/canEditProductionQuestion';
import { STATUS_OPTIONS, getQuestionContextLineItemId } from './constants';
import ResolutionModal, { ResolutionFormValues } from './ResolutionModal';

interface QuestionStatusControlProps {
	question: ProductionQuestion;
	lineItemId?: string;
	disabled?: boolean;
	className?: string;
	/** Table cells use borderless full-width; forms use a bordered fixed-width select. */
	mode?: 'inline' | 'field';
	onUpdated?: (updated: ProductionQuestion) => void;
}

const FIELD_SELECT_WIDTH = 220;

const QuestionStatusControl: React.FC<QuestionStatusControlProps> = ({
	question,
	lineItemId,
	disabled = false,
	className,
	mode = 'inline',
	onUpdated,
}) => {
	const isField = mode === 'field';
	const isResolved = question.status === 'resolved';
	const { data: loggedInUser } = useGetLoggedInUserQuery();
	const canEdit = canEditProductionQuestion(loggedInUser, question);
	const [updateQuestion, { isLoading }] = useUpdateProductionQuestionMutation();
	const [resolutionOpen, setResolutionOpen] = useState(false);
	const [pendingStatus, setPendingStatus] =
		useState<ProductionQuestionStatus | null>(null);

	const submitStatusChange = async (
		status: ProductionQuestionStatus,
		resolution?: ResolutionFormValues
	) => {
		try {
			const updated = await updateQuestion({
				questionId: question.id,
				lineItemId: getQuestionContextLineItemId(question, lineItemId),
				status,
				resolution_category: resolution?.resolution_category,
				resolution_source: resolution?.resolution_source,
				resolution_description: resolution?.resolution_description,
			}).unwrap();
			onUpdated?.(updated);
			message.success('Status updated');
		} catch {
			message.error('Failed to update status');
		}
	};

	const handleStatusChange = async (nextStatus: ProductionQuestionStatus) => {
		if (nextStatus === question.status) return;

		if (nextStatus === 'resolved') {
			setPendingStatus(nextStatus);
			setResolutionOpen(true);
			return;
		}

		await submitStatusChange(nextStatus);
	};

	const handleResolutionSubmit = async (values: ResolutionFormValues) => {
		if (!pendingStatus) return;

		await submitStatusChange(pendingStatus, values);
		setResolutionOpen(false);
		setPendingStatus(null);
	};

	return (
		<>
			<div
				className={className ?? (isField ? undefined : 'w-full min-w-0')}
				onClick={(event) => event.stopPropagation()}
			>
				<Select
					variant={isField ? 'outlined' : 'borderless'}
					value={question.status}
					options={STATUS_OPTIONS}
					disabled={disabled || isLoading || isResolved || !canEdit}
					loading={isLoading}
					className={isField ? undefined : 'w-full'}
					style={isField ? { width: FIELD_SELECT_WIDTH } : { width: '100%' }}
					popupMatchSelectWidth={false}
					onChange={handleStatusChange}
				/>
			</div>

			<ResolutionModal
				open={resolutionOpen}
				initialCategory={question.resolution_category ?? ''}
				initialSource={question.resolution_source ?? ''}
				initialDescription={question.resolution_description}
				confirmLoading={isLoading}
				onCancel={() => {
					setResolutionOpen(false);
					setPendingStatus(null);
				}}
				onSubmit={handleResolutionSubmit}
			/>
		</>
	);
};

export default QuestionStatusControl;
